/**
 * Error classification for Auth0 and token exchange failures.
 * Used to decide retry vs logout. 'offline' = browser offline (wait for online); 'network' = transient network issue (retry).
 */
type TokenErrorClassification =
  | 'auth_required'
  | 'network'
  | 'offline'
  | 'unknown';

/**
 * Error record type for internal use.
 */
type ErrorRecord = Record<string, unknown>;

/**
 * Type guard: true when value is a non-null object (ErrorRecord).
 */
function isErrorRecord(value: unknown): value is ErrorRecord {
  return value !== null && typeof value === 'object';
}

/**
 * Converts an unknown error to a record for classification (normalizes null/undefined/non-object).
 */
function toErrorRecord(error: unknown): ErrorRecord {
  return isErrorRecord(error) ? error : {};
}

/**
 * Auth0/OIDC error codes that require re-authentication (no retry).
 * From Auth0 Configure Silent Authentication + OIDC Core 3.1.2.6 + token endpoint (invalid_grant).
 */
const AUTH_REQUIRED_ERROR_CODES = new Set([
  'login_required', // User's session has expired or they're not logged in
  'consent_required', // User needs to give consent to authorize the application
  'interaction_required', // User needs to complete some interaction (e.g., redirect rule)
  'account_selection_required', // User has multiple accounts and must select one
  'mfa_required', // User needs to complete MFA
  'invalid_grant', // OAuth 2.0 RFC 6749 §5.2: refresh token/auth grant invalid, expired, or revoked
]);

/**
 * Error codes that indicate a network/transient failure (Axios + Node.js).
 * @see https://nodejs.org/api/errors.html#common-system-errors
 */
const NETWORK_AXIOS_CODES = [
  'econnaborted', // Request aborted or timed out
  'econnrefused', // Connection refused by server
  'econnreset', // Connection reset by peer
  'etimedout', // Connection timed out
  'err_network', // Axios generic network error
  'enotfound', // DNS resolution failed
  'enetunreach', // Network unreachable
  'ehostunreach', // Host unreachable
];

/**
 * Checks if browser is offline or error has navigatorOnLine property set to false.
 * @param error - The error to check for offline status.
 */
function isOffline(error: ErrorRecord): boolean {
  return (
    error.navigatorOnLine === false ||
    (typeof navigator !== 'undefined' && !navigator.onLine)
  );
}

/**
 * Checks if error has code property set to a value in NETWORK_AXIOS_CODES.
 * @param error - The error to check for network axios code.
 */
function hasNetworkAxiosCode(error: ErrorRecord): boolean {
  const code = (error.code as string)?.toLowerCase();
  return NETWORK_AXIOS_CODES.includes(code);
}

/**
 * Returns the response status from the error.
 * @param error - The error to get the response status from.
 */
function getResponseStatus(error: ErrorRecord): number {
  return (
    (error.response as { status?: number })?.status ??
    (error.status as number) ??
    0
  );
}

/**
 * Classifies Auth0 getAccessTokenSilently errors.
 * @param error - The error to classify.
 * @returns The classification of the error.
 */
function classifyAuth0Error(error: ErrorRecord): TokenErrorClassification {
  if (!isErrorRecord(error)) return 'unknown';

  const code = typeof error.error === 'string' ? error.error.toLowerCase() : '';

  if (AUTH_REQUIRED_ERROR_CODES.has(code)) {
    return 'auth_required';
  }

  if (isOffline(error)) return 'offline';
  if (code === 'timeout' || code === 'request_error') return 'network';
  if (hasNetworkAxiosCode(error)) return 'network';

  return 'unknown';
}

/**
 * Classifies errors from auth0ToBHiveToken (Portal API call).
 * Uses Axios response status and network indicators.
 */
function classifyTokenExchangeError(
  error: ErrorRecord
): TokenErrorClassification {
  if (!isErrorRecord(error)) return 'unknown';

  const status = getResponseStatus(error);
  const message = (error.message as string) || '';

  if (status === 401) return 'auth_required';
  if (isOffline(error)) return 'offline';
  if (hasNetworkAxiosCode(error)) return 'network';
  if (status === 0 && message) return 'network'; // CORS, no response

  return 'unknown';
}

/**
 * Returns a single classification for breadcrumbs/logging (Auth0 first, else token exchange).
 * @param error - The error to get the classification for.
 */
export function getTokenExchangeClassification(
  error: unknown
): TokenErrorClassification {
  const record = toErrorRecord(error);
  const auth0Error = classifyAuth0Error(record);
  return auth0Error !== 'unknown'
    ? auth0Error
    : classifyTokenExchangeError(record);
}

/**
 * Checks if error is from Auth0/token flow and requires re-authentication.
 * @param error - The error to check for auth required status.
 */
export function isAuthRequiredError(error: unknown): boolean {
  const record = toErrorRecord(error);
  return (
    classifyAuth0Error(record) === 'auth_required' ||
    classifyTokenExchangeError(record) === 'auth_required'
  );
}

/**
 * True when the browser is completely offline. Caller should not retry; wait for online event.
 * @param error - The error (or record) to classify.
 */
export function isOfflineError(error: unknown): boolean {
  const record = toErrorRecord(error);
  return (
    classifyAuth0Error(record) === 'offline' ||
    classifyTokenExchangeError(record) === 'offline'
  );
}
